import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../api/useHttpAdapter";
import { BrandDetails } from "../../../types/add-brand-types";
import {
  BrandListResponse,
  ContentLog,
  ContentLogsResponse,
} from "../../../types/dashboard";

interface DashboardState {
  brandList: BrandDetails[] | null;
  contentLogs: ContentLog[] | null;
  loadingBrandList: boolean;
  loadingContentLogs: boolean;
  error: string | null;
}

const initialState: DashboardState = {
  brandList: null,
  contentLogs: null,
  loadingBrandList: false,
  loadingContentLogs: false,
  error: null,
};

export const fetchBrandList = createAsyncThunk(
  "dashboard/fetchBrandList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<BrandListResponse>(
        "/getAllBrand"
      );
      if (response.status === 200) {
        return response.data.data;
      }
      return rejectWithValue("Failed to fetch brands");
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch brands");
    }
  }
);

export const fetchContentLogs = createAsyncThunk(
  "dashboard/fetchContentLogs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<ContentLogsResponse>(
        "/content/history/0/5?include_total=false"
      );
      if (response.status === 200) {
        return response.data.data;
      }
      return rejectWithValue("Failed to fetch content logs");
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch content logs");
    }
  }
);

export const logoutAccount = createAsyncThunk(
  "dashboard/logoutAccount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/identity/logout");
      if (response.status === 200) {
        return response.data;
      }
      return null;
    } catch (error: any) {
      // Logout usually shouldn't block UI if it fails
      return null;
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboard: (state) => {
      state.brandList = null;
      state.contentLogs = null;
      state.loadingBrandList = false;
      state.loadingContentLogs = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Brand List
      .addCase(fetchBrandList.pending, (state) => {
        state.loadingBrandList = true;
        state.error = null;
      })
      .addCase(fetchBrandList.fulfilled, (state, action) => {
        state.loadingBrandList = false;
        state.brandList = action.payload;
      })
      .addCase(fetchBrandList.rejected, (state, action) => {
        state.loadingBrandList = false;
        state.error = action.payload as string;
      })
      // Content Logs
      .addCase(fetchContentLogs.pending, (state) => {
        state.loadingContentLogs = true;
        state.error = null;
      })
      .addCase(fetchContentLogs.fulfilled, (state, action) => {
        state.loadingContentLogs = false;
        state.contentLogs = action.payload;
      })
      .addCase(fetchContentLogs.rejected, (state, action) => {
        state.loadingContentLogs = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;
